import { Box } from '@material-ui/core';
import clsx from 'clsx';

import { SInput, SText } from '@setvi/shared/components';
import { SetviSwitch } from '@setvi/shared/components/sforms/sformik/formik-switch';
import type { Resource } from '@setvi/shared/interfaces';

import type { CoverPage } from '../../../../api/cover-templates';
import type { CoverFieldKey, CoverForm } from '../../../../types';
import { COVER_FIELDS } from '../../constants';
import { CoverLogo } from '../cover-logo';
import { CoverThumb } from '../cover-thumb';
import { useStyles } from './styles';

interface CoverSectionProps {
  resource: Resource;
  templates: CoverPage[];
  templatesLoading: boolean;
  /** False when the backend turns cover pages off for this resource. */
  available: boolean;
  /** The backend's reason, shown in place of the cover pages. */
  message: string | null;
  /** The toggle: whether the rep wants a cover page at all. */
  active: boolean;
  setActive: (next: boolean) => void;
  templateId: string | null;
  /** Picks a cover and generates it; picking it again regenerates. */
  select: (id: string) => void;
  form: CoverForm;
  errors: Partial<Record<CoverFieldKey, string>>;
  /** True while a detail is invalid, so no cover can be generated yet. */
  hasErrors: boolean;
  setField: (key: CoverFieldKey, value: string) => void;
  /** A picked file replaces the logo; null removes it. */
  setLogo: (file: File | null) => void;
  /** Back to the company logo, undoing an upload or a removal. */
  resetLogo: () => void;
  onLogoError: (message: string) => void;
  defaultLogoUrl: string | null;
  /** True while the selected cover is being generated. */
  generating: boolean;
}

export const CoverSection = ({
  resource,
  templates,
  templatesLoading,
  available,
  message,
  active,
  setActive,
  templateId,
  select,
  form,
  errors,
  hasErrors,
  setField,
  setLogo,
  resetLogo,
  onLogoError,
  defaultLogoUrl,
  generating
}: CoverSectionProps) => {
  const classes = useStyles();

  // Undefined keeps the company logo, '' means the rep removed it, anything
  // else is their own upload.
  const isCompanyLogo = form.logoBase64 === undefined;
  const logoSrc = isCompanyLogo ? defaultLogoUrl : form.logoBase64;

  return (
    <Box>
      <Box className={classes.toggleRow}>
        <SText maxLines="unset" noLineClamp>
          <b>Optional:</b> Add a personalized cover page with your logo or
          headshot and contact details. It prints as a separate sheet with your
          order.
        </SText>
        <SetviSwitch
          checked={active}
          disabled={generating}
          onChange={event => setActive(event.target.checked)}
        />
      </Box>

      {active && (
        <>
          <Box mt={2}>
            <CoverLogo
              src={logoSrc}
              isCompanyLogo={isCompanyLogo}
              hasCompanyLogo={!!defaultLogoUrl}
              disabled={generating}
              onSelect={file => setLogo(file)}
              onRemove={() => setLogo(null)}
              onReset={resetLogo}
              onError={onLogoError}
            />
          </Box>

          <Box mt={2} className={classes.fieldsColumn}>
            {COVER_FIELDS.map(field => (
              <SInput
                key={field.key}
                fullWidth
                newDesign
                type={field.type}
                label={field.label}
                placeholder={field.placeholder}
                value={form.values[field.key]}
                disabled={generating}
                error={!!errors[field.key]}
                helperText={errors[field.key]}
                onChange={e => setField(field.key, e.target.value)}
              />
            ))}
          </Box>
          <SText
            title="Select the cover page below to generate. After making any changes, select it again to refresh, review, then add to cart."
            size="xs"
            noLineClamp
            fontColor="#5D7083"
          />

          {hasErrors && (
            <SText
              title="Fix the highlighted details to select a cover page."
              size="xs"
              noLineClamp
              fontColor="#D72638"
            />
          )}

          <Box mt={2}>
            {!templatesLoading && !available && (
              <Box className={classes.emptyTemplates}>
                <SText
                  title={
                    message || 'Cover pages are not available for this asset.'
                  }
                  size="sm"
                  maxLines="unset"
                  fontColor="#5D7083"
                />
              </Box>
            )}

            {!templatesLoading && available && templates.length > 0 && (
              <Box className={classes.templatesRow}>
                {templates.map(template => (
                  <Box
                    key={template.FileName}
                    className={clsx(classes.templateThumb, {
                      [classes.templateThumbSelected]:
                        templateId === template.FileName,
                      [classes.templateThumbDisabled]: hasErrors || generating
                    })}
                    onClick={() => select(template.FileName)}>
                    <CoverThumb template={template} resource={resource} />
                  </Box>
                ))}
              </Box>
            )}

            {!templatesLoading && available && templates.length === 0 && (
              <Box className={classes.emptyTemplates}>
                <SText
                  title="No cover templates available yet."
                  size="sm"
                  maxLines="unset"
                  fontColor="#5D7083"
                />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
